import React, { useEffect, useState, useRef } from 'react';
import { X, Loader2, Upload, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';
const CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'des9fk8et';
const UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || 'frg_profile_photos';

const labelCls =
  'font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/60 mb-1.5 block';
const inputCls =
  'w-full bg-white border border-[#EFE6CF] text-[#0A1F2E] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#2CA8A0] focus:ring-2 focus:ring-[#2CA8A0]/25 transition';

const loadCloudinaryScript = () =>
  new Promise((resolve) => {
    if (window.cloudinary) return resolve(true);
    const s = document.createElement('script');
    s.src = 'https://upload-widget.cloudinary.com/latest/global/all.js';
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.head.appendChild(s);
  });

/**
 * Create-Group modal (iter 76 Phase 3).
 *
 * Fields: name, description, city (live list from /api/cities), optional
 * Cloudinary photo. POST /api/peer-circles/groups creates the group with
 * the creator auto-joined; we hand the new group back via `onCreated`.
 */
const CreateGroupModal = ({ open, onClose, onCreated }) => {
  const [form, setForm] = useState({ name: '', description: '', city: '', photo_url: '' });
  const [cities, setCities] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const widgetRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    fetch(`${API}/api/cities`)
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => {
        const items = Array.isArray(d) ? d : d.cities || [];
        setCities(items.map((c) => c.name).filter(Boolean).sort());
      })
      .catch(() => {});
    loadCloudinaryScript();
    return undefined;
  }, [open]);

  if (!open) return null;

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const openUpload = () => {
    if (!window.cloudinary || !CLOUD_NAME || !UPLOAD_PRESET) {
      toast.error('Photo upload is not configured.');
      return;
    }
    setUploading(true);
    widgetRef.current = window.cloudinary.createUploadWidget(
      {
        cloudName: CLOUD_NAME,
        uploadPreset: UPLOAD_PRESET,
        sources: ['local', 'url'],
        multiple: false,
        resourceType: 'image',
        folder: 'mundalo_peer_circle_groups',
        maxFileSize: 8_000_000,
        cropping: false,
      },
      (err, result) => {
        if (err) {
          setUploading(false);
          toast.error('Photo upload failed.');
          return;
        }
        if (result && result.event === 'success') {
          set('photo_url', result.info.secure_url);
        }
        if (result && (result.event === 'close' || result.event === 'queues-end')) {
          setUploading(false);
        }
      },
    );
    widgetRef.current.open();
  };

  const submit = async (e) => {
    e.preventDefault();
    const name = form.name.trim();
    const desc = form.description.trim();
    const city = (form.city || '').trim();
    if (name.length < 2) return toast.error('Group name is too short.');
    if (desc.length < 10) return toast.error('Description must be at least 10 characters.');
    if (!city) return toast.error('Pick a city.');

    setSubmitting(true);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API}/api/peer-circles/groups`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          name,
          description: desc,
          city,
          photo_url: form.photo_url || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail?.message || data.detail || 'Could not create group.');
      toast.success('Group created.');
      onCreated?.(data.group);
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-[#0A1F2E]/45 flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="create-group-modal"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="bg-white border border-[#EFE6CF] rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
      >
        <header className="flex items-center justify-between px-5 py-4 border-b border-[#EFE6CF] sticky top-0 bg-white rounded-t-2xl">
          <h2 className="font-display italic text-2xl text-[#0A1F2E]">Create a group</h2>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full hover:bg-[#F4ECD8] flex items-center justify-center transition"
            data-testid="create-group-close"
          >
            <X className="w-4 h-4 text-[#0A1F2E]" />
          </button>
        </header>

        <div className="p-5 space-y-4">
          <div>
            <label className={labelCls}>Group name</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => set('name', e.target.value.slice(0, 80))}
              maxLength={80}
              className={inputCls}
              placeholder="e.g. Tulum Beach Coworkers"
              data-testid="create-group-name"
            />
          </div>

          <div>
            <label className={labelCls}>Description</label>
            <textarea
              value={form.description}
              onChange={(e) => set('description', e.target.value.slice(0, 600))}
              maxLength={600}
              rows={4}
              className={`${inputCls} resize-none`}
              placeholder="What is this group about? Who should join?"
              data-testid="create-group-description"
            />
            <p className="text-right text-[10px] text-[#0A1F2E]/50 mt-1">{(form.description || '').length}/600</p>
          </div>

          <div>
            <label className={labelCls}>City</label>
            <select
              value={form.city}
              onChange={(e) => set('city', e.target.value)}
              className={inputCls}
              data-testid="create-group-city"
            >
              <option value="">Select a city</option>
              {cities.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          <div>
            <label className={labelCls}>Group photo (optional)</label>
            <div className="flex items-center gap-3">
              {form.photo_url ? (
                <div className="relative">
                  <img
                    src={form.photo_url}
                    alt=""
                    className="w-20 h-20 rounded-lg object-cover ring-2 ring-[#EFE6CF]"
                    data-testid="create-group-photo-preview"
                  />
                  <button
                    type="button"
                    onClick={() => set('photo_url', '')}
                    className="absolute -top-2 -right-2 bg-white border border-[#EFE6CF] rounded-full p-1 hover:bg-[#F4ECD8] transition"
                    data-testid="create-group-photo-remove"
                  >
                    <Trash2 className="w-3 h-3 text-[#B91C1C]" />
                  </button>
                </div>
              ) : null}
              <button
                type="button"
                onClick={openUpload}
                disabled={uploading}
                className="inline-flex items-center gap-2 bg-white border border-[#0A1F2E]/20 hover:border-[#0A1F2E]/45 text-[#0A1F2E] font-mono uppercase text-[10px] tracking-[0.15em] px-4 py-2.5 rounded-full transition"
                data-testid="create-group-photo-upload"
              >
                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                {form.photo_url ? 'Replace photo' : 'Upload photo'}
              </button>
            </div>
          </div>
        </div>

        <footer className="px-5 py-4 border-t border-[#EFE6CF] flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="font-mono uppercase text-[10px] tracking-[0.15em] bg-white border border-[#0A1F2E]/20 hover:border-[#0A1F2E]/45 text-[#0A1F2E] px-4 py-2 rounded-full transition"
            data-testid="create-group-cancel"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="font-mono uppercase text-[10px] tracking-[0.15em] bg-[#0A1F2E] hover:bg-[#163448] disabled:opacity-50 text-white px-5 py-2 rounded-full transition inline-flex items-center gap-2"
            data-testid="create-group-submit"
          >
            {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Create group
          </button>
        </footer>
      </form>
    </div>
  );
};

export default CreateGroupModal;
